import React, { useState } from "react";
import axios from "axios";
import { useT, useLang } from "../context/LanguageContext";
import { t } from "../data/translations";
import { TID } from "../constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ADDRESS_QUERY = encodeURIComponent(
    "Shop No. 1, Shri Ayyappa Krupa Bungalow, Sector 5, Shreenagar, Wagle Estate, Thane 400604"
);
const MAPS_EMBED = `https://www.google.com/maps?q=${ADDRESS_QUERY}&output=embed`;
const MAPS_LINK = `https://www.google.com/maps/search/?api=1&query=${ADDRESS_QUERY}`;
const WHATSAPP_LINK = (msg) => `https://wa.me/919987070049?text=${encodeURIComponent(msg)}`;

const InfoRow = ({ icon, label, children, accent = "text-red-700" }) => (
    <div className="flex gap-4">
        <div className={`shrink-0 w-11 h-11 rounded-lg bg-red-50 ${accent} flex items-center justify-center`}>
            <i className={`fa-solid ${icon} text-lg`} />
        </div>
        <div>
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-0.5">{label}</div>
            <div className="text-slate-800 text-[15px] leading-relaxed">{children}</div>
        </div>
    </div>
);

const Contact = () => {
    const tr = useT();
    const { lang } = useLang();

    const [form, setForm] = useState({ name: "", mobile: "", email: "", query_type: "", message: "" });
    const [status, setStatus] = useState({ loading: false, ok: null, error: "" });

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setStatus({ loading: true, ok: null, error: "" });
        try {
            const payload = Object.fromEntries(
                Object.entries(form).filter(([, v]) => v !== "" && v !== null && v !== undefined)
            );
            const res = await axios.post(`${API}/contact`, payload);
            if (res.data?.success) {
                setStatus({ loading: false, ok: true, error: "" });
                setForm({ name: "", mobile: "", email: "", query_type: "", message: "" });
            } else {
                setStatus({ loading: false, ok: false, error: tr(t.form.error) });
            }
        } catch {
            setStatus({ loading: false, ok: false, error: tr(t.form.error) });
        }
    };

    const queryTypes = [
        { en: "General Inquiry", mr: "सर्वसाधारण चौकशी" },
        { en: "Deposit Schemes", mr: "ठेवी योजना" },
        { en: "Loan Inquiry", mr: "कर्ज चौकशी" },
        { en: "Membership", mr: "सभासदत्व" },
        { en: "Complaint / Feedback", mr: "तक्रार / सूचना" }
    ];

    return (
        <div>
            <section className="bg-gradient-to-br from-slate-900 to-red-900 text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">{tr(t.contact.title)}</h1>
                    <p className="mt-4 text-slate-300 max-w-2xl text-base sm:text-lg">
                        {lang === "mr"
                            ? "आम्हाला भेट द्या, फोन करा किंवा संदेश पाठवा. आमची टीम तुम्हाला नक्की मदत करेल."
                            : "Visit us, give us a call, or send a message — our team is always ready to help."}
                    </p>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
                <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10">
                    {/* Info column */}
                    <div className="space-y-6">
                        <InfoRow icon="fa-location-dot" label={tr(t.contact.addressLabel)}>
                            {tr(t.contact.address)}
                        </InfoRow>
                        <InfoRow icon="fa-phone" label={tr(t.contact.mobileLabel)} accent="text-amber-700">
                            <div className="flex flex-wrap gap-x-3 gap-y-1">
                                <a href="tel:+919987070049" className="hover:text-red-700 font-semibold">9987070049</a>
                                <span className="text-slate-400">/</span>
                                <a href="tel:+918591385792" className="hover:text-red-700 font-semibold">8591385792</a>
                            </div>
                        </InfoRow>
                        <InfoRow icon="fa-clock" label={tr(t.contact.hoursLabel)} accent="text-amber-700">
                            <div className="space-y-0.5">
                                <div>{tr(t.hours.weekdays)}</div>
                                <div className="text-sm text-slate-600">{tr(t.hours.lunch)}</div>
                                <div className="text-sm text-red-700 font-semibold">{tr(t.hours.sunday)}</div>
                            </div>
                        </InfoRow>

                        {/* Map */}
                        <div className="rounded-xl overflow-hidden border border-slate-200 shadow-sm">
                            <iframe
                                title="Sukhakarta Patsanstha Location"
                                src={MAPS_EMBED}
                                width="100%"
                                height="260"
                                style={{ border: 0 }}
                                allowFullScreen=""
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                            />
                            <div className="bg-white px-4 py-3 flex items-center justify-between">
                                <span className="text-xs text-slate-500">{lang === "mr" ? "गूगल नकाशा" : "Google Maps"}</span>
                                <a
                                    href={MAPS_LINK}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-red-700 text-sm font-semibold hover:underline inline-flex items-center gap-1"
                                >
                                    {lang === "mr" ? "नकाशावर पहा" : "Open in Maps"}
                                    <i className="fa-solid fa-arrow-up-right-from-square text-xs" />
                                </a>
                            </div>
                        </div>

                        <a
                            href={WHATSAPP_LINK(
                                lang === "mr"
                                    ? "नमस्कार! मला सुखकर्ता पतपेढीच्या सेवांविषयी माहिती हवी आहे."
                                    : "Hello! I want to know about Sukhakarta Patsanstha services."
                            )}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={TID.contactWhatsapp}
                            className="inline-flex items-center justify-center gap-2 w-full bg-green-500 hover:bg-green-600 text-white font-semibold px-5 py-3.5 rounded-lg transition-colors"
                        >
                            <i className="fa-brands fa-whatsapp text-lg" />
                            {tr(t.cta.whatsapp)}
                        </a>
                    </div>

                    {/* Form */}
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8">
                        <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-1">{tr(t.contact.formTitle)}</h3>
                        <p className="text-sm text-slate-500 mb-6">
                            {lang === "mr" ? "खालील फॉर्म भरा. आम्ही तुमच्याशी संपर्क साधू." : "Fill the form below and we'll get back to you."}
                        </p>

                        <form onSubmit={submit} className="space-y-4">
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                                        {tr(t.form.name)} *
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        value={form.name}
                                        onChange={update("name")}
                                        data-testid={TID.contactName}
                                        className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-red-700 focus:ring-2 focus:ring-red-700/20 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                                        {tr(t.form.mobile)} *
                                    </label>
                                    <input
                                        type="tel"
                                        required
                                        value={form.mobile}
                                        onChange={update("mobile")}
                                        data-testid={TID.contactMobile}
                                        className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-red-700 focus:ring-2 focus:ring-red-700/20 outline-none"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                                    {tr(t.form.email)}
                                </label>
                                <input
                                    type="email"
                                    value={form.email}
                                    onChange={update("email")}
                                    data-testid={TID.contactEmail}
                                    className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-red-700 focus:ring-2 focus:ring-red-700/20 outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                                    {tr(t.form.queryType)}
                                </label>
                                <select
                                    value={form.query_type}
                                    onChange={update("query_type")}
                                    data-testid={TID.contactQuery}
                                    className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-red-700 focus:ring-2 focus:ring-red-700/20 outline-none bg-white"
                                >
                                    <option value="">— {lang === "mr" ? "निवडा" : "Select"} —</option>
                                    {queryTypes.map((q, i) => (
                                        <option key={i} value={q.en}>{tr(q)}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                                    {tr(t.form.message)} *
                                </label>
                                <textarea
                                    rows={5}
                                    required
                                    value={form.message}
                                    onChange={update("message")}
                                    data-testid={TID.contactMessage}
                                    className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-red-700 focus:ring-2 focus:ring-red-700/20 outline-none resize-none"
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={status.loading}
                                data-testid={TID.contactSubmit}
                                className="w-full inline-flex items-center justify-center gap-2 bg-red-700 hover:bg-red-800 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold px-5 py-3.5 rounded-lg transition-colors shadow-sm"
                            >
                                {status.loading ? tr(t.cta.sending) : tr(t.cta.submit)}
                                {!status.loading && <i className="fa-solid fa-paper-plane" />}
                            </button>

                            {status.ok && (
                                <div
                                    data-testid={TID.contactSuccess}
                                    className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm font-semibold flex items-center gap-2"
                                >
                                    <i className="fa-solid fa-circle-check" />
                                    {tr(t.form.thankYou)}
                                </div>
                            )}
                            {status.error && (
                                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm font-semibold flex items-center gap-2">
                                    <i className="fa-solid fa-triangle-exclamation" />
                                    {status.error}
                                </div>
                            )}
                        </form>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Contact;
